import React, { useRef, useEffect, useState } from "react";
import { cn } from "@pineseed/gui/util/utils";
import { FaceTracker } from "./FaceTracker";
import { Ripple } from "./types";
import { rippleHandler } from "./handlers/rippleHandler";
import "./avatar-dome.css";

type AvatarDomeFaceTrackerProps =
  | { useFaceTracker: true; facesBasePath: string }
  | { useFaceTracker?: false; facesBasePath?: never };

type AvatarDomeProps = AvatarDomeFaceTrackerProps & {
  children?: React.ReactNode;
  image?: string;
  className?: string;
  size: number | string;
  onClick?: (event: MouseEvent) => void;
};

export const AvatarDome: React.FC<AvatarDomeProps> = (props) => {
  const { children, image, className, size, onClick } = props;
  const domeRef = useRef<HTMLDivElement>(null);
  const mouseReflectionRef = useRef<HTMLDivElement>(null);
  const mouseShadowRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [ripples, setRipples] = useState<Ripple[]>([]);

  // Normalize size to pixels
  const sizeInPx = typeof size === "number" ? `${size}px` : size;

  useEffect(() => {
    const dome = domeRef.current;
    const mouseReflection = mouseReflectionRef.current;
    const mouseShadow = mouseShadowRef.current;

    if (!dome || !mouseReflection || !mouseShadow) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isHovered) return;

      const rect = dome.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate mouse position relative to dome center
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;

      // Convert to percentage of dome radius
      const radius = rect.width / 2;
      const relativeX = (mouseX / radius) * 50; // Scale to percentage
      const relativeY = (mouseY / radius) * 50;

      // Constrain to dome bounds
      const distance = Math.sqrt(relativeX * relativeX + relativeY * relativeY);
      const maxDistance = 45; // Stay within dome bounds

      let finalX = relativeX;
      let finalY = relativeY;

      if (distance > maxDistance) {
        const scale = maxDistance / distance;
        finalX = relativeX * scale;
        finalY = relativeY * scale;
      }

      // Position reflection (inverted for glass dome effect)
      const reflectionX = 50 - finalX * 0.3; // Subtle inversion and dampening
      const reflectionY = 50 - finalY * 0.3;

      // Position shadow (follows mouse more directly but dampened)
      const shadowX = 50 + finalX * 0.4;
      const shadowY = 50 + finalY * 0.4;

      // Apply transforms
      mouseReflection.style.left = `${reflectionX}%`;
      mouseReflection.style.top = `${reflectionY}%`;

      mouseShadow.style.left = `${shadowX}%`;
      mouseShadow.style.top = `${shadowY}%`;

      // Adjust opacity based on distance from center
      const maxOpacity = 0.8;
      const reflectionOpacity = Math.max(
        0.2,
        maxOpacity - (distance / maxDistance) * 0.4,
      );
      const shadowOpacity = Math.max(0.1, 0.4 - (distance / maxDistance) * 0.2);

      mouseReflection.style.opacity = reflectionOpacity.toString();
      mouseShadow.style.opacity = shadowOpacity.toString();
    };

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      // Reset positions to default
      if (mouseReflection && mouseShadow) {
        mouseReflection.style.opacity = "0";
        mouseShadow.style.opacity = "0";
      }
    };

    const handleClick = (e: MouseEvent) => {
      rippleHandler({
        event: e,
        domeElement: dome,
        setRipples,
      });

      // Call the external click handler if provided
      if (onClick) {
        onClick(e);
      }
    };

    dome.addEventListener("mousemove", handleMouseMove);
    dome.addEventListener("mouseenter", handleMouseEnter);
    dome.addEventListener("mouseleave", handleMouseLeave);
    dome.addEventListener("click", handleClick);

    return () => {
      dome.removeEventListener("mousemove", handleMouseMove);
      dome.removeEventListener("mouseenter", handleMouseEnter);
      dome.removeEventListener("mouseleave", handleMouseLeave);
      dome.removeEventListener("click", handleClick);
    };
  }, [isHovered, onClick]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        ref={domeRef}
        className={cn("avatar-dome", className)}
        style={{
          width: sizeInPx,
          height: sizeInPx,
        }}
      >
        {props.useFaceTracker ? (
          <FaceTracker basePath={props.facesBasePath} />
        ) : image ? (
          <img src={image} alt="Avatar" />
        ) : (
          children
        )}

        {/* Glass dome effects */}
        {/* Secondary highlight for depth */}
        <div className="secondary-highlight" />

        {/* Dynamic mouse reflection */}
        <div ref={mouseReflectionRef} className="mouse-reflection" />

        {/* Dynamic mouse shadow */}
        <div ref={mouseShadowRef} className="mouse-shadow" />

        {/* Ripple effects */}
        {ripples.map((ripple) => (
          <div
            key={ripple.id}
            className="ripple"
            style={{
              left: `${ripple.x}px`,
              top: `${ripple.y}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
};


